import { Link } from "@inertiajs/react"
import { ExternalLink, Megaphone } from "lucide-react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { BrandLogo } from "@/components/brand-logo"
import { CTAButton } from "@/components/collab-card/cta-button"
import { DeclineOverflow } from "@/components/collab-card/decline-overflow"
import { StateBadge } from "@/components/collab-card/state-badge"
import { ActionRequiredBadge } from "@/components/mission-card"
import { GiftLine } from "@/components/mission-card/gift-line"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { toAppLocale } from "@/lib/i18n/locales"
import { cn } from "@/lib/utils"
import { creatorMissionDetailPath } from "@/routes"
import type { Locale } from "@/types"
import type { Collab } from "@/types/collab"

/**
 * OKS-33 — Promotional creator card (UX Spec §3).
 *
 * Mirrors the shape of the generic `CollabCard` (top row, title,
 * deadline, state badge, CTA) but adds a Promotional-specific body
 * between the state badge and the CTA:
 *
 *   - `assigned`  → "Buy on {brand} site, post on TikTok." helper.
 *                   CTA = Accept (handled by `CTAButton`).
 *   - `accepted`  → Copyable `promo_code` block + Buy-on-brand button
 *                   (external link to `mission.ecommerce_url`). The
 *                   wait-state helper drops the user a hint that the
 *                   brand is watching for the order.
 *   - `purchased`/`to_ship`/`shipped`/`delivered` → state-specific
 *                   wait-state helper. CTA is `null` for the brand-
 *                   driven states; `mark_posted` lights up at
 *                   `delivered` (UI in OKS-35).
 *   - `posted` / `completed` / `issue` / `declined` → mirrors Drop /
 *                   Open posture; helper renders the appropriate
 *                   "your post is in" / "completed" / "issue" / "you
 *                   declined" copy.
 *
 * Brand isolation: the card never reaches into `Collab.brand` for
 * anything cross-brand — `mission.promo_code` and `mission.ecommerce_url`
 * are scoped to the creator's own collab by `CreatorMyMissionsQuery`.
 *
 * OKS-611 — the whole card is now a link to the §08 mission detail,
 * mirroring the shipped `MissionCard` (§05) stretched-overlay pattern:
 * an `absolute inset-0 z-0` Inertia `<Link>` covers the card, sitting
 * *beneath* the interactive controls, which are lifted to `relative
 * z-10`. Plain content (brand row, title, state badge, helper text)
 * stays static — the positioned overlay paints above it, so a tap
 * anywhere on the body navigates to the detail. Unlike `MissionCard`'s
 * `linkMode` gating, the promotional card is ALWAYS a whole-card link,
 * in every lifecycle state (TRD KD-3). No `stopPropagation` is needed:
 * the overlay is a *sibling* of the controls, not their ancestor, so a
 * tap on a `z-10` control never bubbles to the `z-0` link (TRD KD-2).
 */
interface PromotionalMissionCardProps {
  collab: Collab
  /** When true, CTA handler routes to onOfflineTap instead of navigating. */
  offline?: boolean
  onOfflineTap?: () => void
  /**
   * OKS-445 — opens the OKS-35 TikTok post picker when the `delivered`
   * submission-ready `submit` CTA is tapped. Mirrors `CollabCard`; lifted by
   * the parent list item.
   */
  onSubmitTap?: () => void
  /**
   * OKS-637 — when `true`, renders the amber "Action required" badge in the
   * title row. Driven by the page's `ready_to_submit_ids` set — the SAME signal
   * behind the Missions-tab count. Badged here (not only on `MissionCard`) so a
   * promotional ready-to-submit collab still reconciles with the tab total
   * (TRD KD-2). Defaults `false`.
   */
  actionRequired?: boolean
  className?: string
}

export function PromotionalMissionCard({
  collab,
  offline,
  onOfflineTap,
  onSubmitTap,
  actionRequired = false,
  className,
}: PromotionalMissionCardProps) {
  const { t, i18n } = useTranslation()
  const locale: Locale = toAppLocale(i18n.language)

  return (
    <Card
      data-testid="promotional-mission-card"
      data-collab-id={collab.id}
      data-state={collab.state}
      className={cn(
        "relative gap-3 overflow-hidden rounded-xl p-4",
        // OKS-1014 — focus ring on the Card root, keyed to the stretched
        // link (same mechanism + reason as MissionCard: an outset ring on
        // the `inset-0` link is clipped by this `overflow-hidden` box).
        "has-[>[data-slot=card-link]:focus-visible]:ring-ring has-[>[data-slot=card-link]:focus-visible]:ring-2",
        // OKS-611 — tap affordance parity with MissionCard: subtle
        // active-press feedback so the card visibly reads as tappable.
        "transition-[transform,opacity] motion-safe:active:scale-[0.99] motion-safe:active:opacity-90",
        className,
      )}
    >
      {/* OKS-611 — stretched whole-card link to the §08 mission detail.
          Covers the entire card at z-0; the interactive controls sit
          above via `relative z-10`. The keyboard focus ring is drawn by the
          Card root, keyed to this link's `data-slot="card-link"` (OKS-1014;
          a ring on the link itself is clipped). */}
      <Link
        href={creatorMissionDetailPath(collab.id)}
        aria-label={t("missions.card.open_aria", {
          title: collab.mission.title,
        })}
        className="absolute inset-0 z-0 rounded-xl focus-visible:outline-none"
        data-slot="card-link"
        data-testid="promotional-mission-card-link"
      />

      <CardContent className="flex flex-col gap-3 p-0">
        {/* Top row — brand (left) + Promotional type chip + decline overflow (right) */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {/* OKS-316 — Migrated from shadcn `<Avatar>` block to the
                shared `<BrandLogo>` wrapper (OKS-303 single source of
                truth for brand chrome). Size + shape preserved; the
                fallback visual shifts from `bg-primary text-primary-
                foreground` to the canonical `<BrandInitialMonogram>`
                (TRD KD-5 — consolidation is the point). `alt=""`
                because the adjacent brand name span is the textual
                identity. */}
            <BrandLogo
              logoUrl={collab.brand.logo_url}
              name={collab.brand.name}
              className="size-8 shrink-0 rounded-full"
              testId="promotional-mission-card-brand-logo"
            />
            <span
              className="truncate text-sm font-medium"
              data-testid="promotional-mission-card-brand-name"
            >
              {collab.brand.name}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Badge
              variant="outline"
              data-testid="promotional-mission-card-type-chip"
            >
              <Megaphone aria-hidden="true" />
              {t("creator_home.type.promotional")}
            </Badge>
            {collab.decline_path && (
              // OKS-611 — lifted above the whole-card overlay link so the
              // overflow menu opens on tap instead of navigating to detail.
              <div className="relative z-10">
                <DeclineOverflow
                  declinePath={collab.decline_path}
                  missionTitle={collab.mission.title}
                />
              </div>
            )}
          </div>
        </div>

        {/* Title row — title (clamp-2) + OKS-637 "Action required" badge
            trailing. flex-wrap so the badge drops below the title on narrow
            viewports (UX Spec §5). */}
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3
            className="line-clamp-2 min-w-0 flex-1 text-base font-semibold"
            data-testid="promotional-mission-card-title"
          >
            {collab.mission.title}
          </h3>
          {actionRequired && <ActionRequiredBadge />}
        </div>

        {/* OKS-986 — the pinned product, below the title and above the state
            badge (UX D2.1). Wording is PRODUCT, not gift: on a promotional
            mission the creator buys the item with a promo code, so gift
            wording would be a false statement — `<GiftLine>` derives that from
            `mission.type`, which is `promotional` on every row of this card.

            ⚠️ Static flow content. This card is ALWAYS a whole-card link via a
            `z-0` stretched overlay, so a positioned row here would paint over
            it and eat taps (UX D9). */}
        {/* ⚠️ No `offer` prop, deliberately. OKS-990's multi-product offer is
            DROP-ONLY (out of scope: "promotional and open missions — single
            pinned product, unchanged"), so this card can never be the
            multi-product shape. Passing `collab.offer` here would render the
            "N products to choose from" string on a surface with no picker
            behind it. */}
        <GiftLine
          product={collab.product}
          missionType={collab.mission.type}
          locale={locale}
        />

        {/* State badge */}
        <div className="flex flex-wrap items-center gap-2">
          <StateBadge collab={collab} />
        </div>

        {/* Promotional body — the per-state matrix from UX Spec §3. */}
        <PromotionalBody collab={collab} />

        {/* Issue note alert — surfaces the brand's note to the creator. */}
        {collab.state === "issue" && collab.issue_note && (
          <Alert
            variant="destructive"
            data-testid="promotional-mission-card-issue-note"
            className="text-xs"
          >
            <AlertDescription className="line-clamp-3">
              {t("creator_home.promotional.issue_helper", {
                note: collab.issue_note,
              })}
            </AlertDescription>
          </Alert>
        )}

        {/* CTA — backend-authoritative. `assigned` → Accept; `delivered` →
            `submit` picker entry (OKS-445); other states → disabled
            progress affordance. */}
        {/* OKS-611 — `relative z-10` lifts the CTA above the whole-card
            overlay link so its own handler fires (Accept / submit picker)
            without also navigating to the detail. */}
        <CTAButton
          cta={collab.cta}
          missionTitle={collab.mission.title}
          offline={offline}
          onOfflineTap={onOfflineTap}
          onSubmitTap={onSubmitTap}
          className="relative z-10"
        />
      </CardContent>
    </Card>
  )
}

/**
 * Per-state body renderer (UX Spec §3 matrix).
 *
 * Pure presentation — every branch is a static helper line keyed by
 * `creator_home.promotional.*`. The `accepted` branch is the only
 * interactive state body: it surfaces the promo code (copyable) +
 * the buy-on-brand button (external link to `mission.ecommerce_url`).
 */
function PromotionalBody({ collab }: { collab: Collab }) {
  const { t } = useTranslation()

  switch (collab.state) {
    case "assigned":
      return (
        <p
          className="text-muted-foreground text-sm"
          data-testid="promotional-mission-card-body-assigned"
        >
          {t("creator_home.promotional.assigned_helper", {
            brand: collab.brand.name,
          })}
        </p>
      )
    case "accepted":
      return <AcceptedBody collab={collab} />
    case "purchased":
      return <Helper testId="purchased" copyKey="purchased_helper" />
    case "to_ship":
      // OKS-1001 — the CTA on this same card is corrected by
      // `Collabs::CtaResolver#to_ship_cta`, which drop and promotional share.
      // Leaving this body static would make the card argue with itself: the
      // helper saying the brand is preparing the order directly above a CTA
      // saying the address is outstanding. One branch, keyed on the same
      // server-derived flag the CTA is.
      //
      // ⚠️ Body + CTA only. Promotional deliberately gets NO list chip — this
      // card has never carried a `StatusChip`, and the `ActionRequiredBadge`
      // above is welded to OKS-637's ready-to-submit id set, so driving it from
      // an address flag would make the Missions-tab "N to submit" count
      // over-report (BRIEF AC-3b).
      return collab.address_action_required ? (
        <Helper
          testId="to-ship-awaiting-address"
          copyKey="to_ship_awaiting_address_helper"
        />
      ) : (
        <Helper testId="to-ship" copyKey="to_ship_helper" />
      )
    case "shipped":
      return <Helper testId="shipped" copyKey="shipped_helper" />
    case "delivered":
      return (
        <div className="flex flex-col gap-2">
          <Helper testId="delivered" copyKey="delivered_helper" />
          <p className="text-muted-foreground text-xs">
            {t("creator_home.promotional.post_submission_pending")}
          </p>
        </div>
      )
    case "posted":
      return <Helper testId="posted" copyKey="posted_helper" />
    case "completed":
      return <Helper testId="completed" copyKey="completed_helper" />
    case "declined":
      return <Helper testId="declined" copyKey="declined_helper" />
    case "issue":
      // Body text is rendered via the `Alert` in the parent; nothing here.
      return null
    default:
      // Drop-only states (`applied`, `rejected`) shouldn't reach the
      // Promotional card — but if they do (data drift) we render nothing
      // rather than crash the page.
      return null
  }
}

function Helper({ testId, copyKey }: { testId: string; copyKey: string }) {
  const { t } = useTranslation()
  return (
    <p
      className="text-muted-foreground text-sm"
      data-testid={`promotional-mission-card-body-${testId}`}
    >
      {t(`creator_home.promotional.${copyKey}`)}
    </p>
  )
}

/**
 * `accepted` state body — promo code + ecommerce-url block.
 *
 * Promo code: rendered as a `<code>` block with a copy button. Falls
 * back to "Tap to select" helper when the Clipboard API is denied.
 * Ecommerce URL: rendered as a primary external-link Button. When the
 * brand hasn't set their store URL yet, the button disables and the
 * helper line tells the creator to message the brand.
 */
function AcceptedBody({ collab }: { collab: Collab }) {
  const { t } = useTranslation()
  const promoCode = collab.mission.promo_code
  const ecommerceUrl = collab.mission.ecommerce_url

  function handleCopyCode() {
    if (
      promoCode &&
      typeof navigator !== "undefined" &&
      navigator.clipboard?.writeText
    ) {
      navigator.clipboard
        .writeText(promoCode)
        .then(() => {
          toast.success(t("creator_home.promotional.code_copied_toast"))
        })
        .catch(() => {
          // Best-effort copy; long-press / native gesture still works.
        })
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-muted-foreground text-sm">
        {t("creator_home.promotional.accepted_helper")}
      </p>

      {promoCode && (
        <div className="flex flex-col gap-1">
          <span className="text-muted-foreground text-xs font-medium">
            {t("creator_home.promotional.promo_code_label")}
          </span>
          <button
            type="button"
            onClick={handleCopyCode}
            aria-label={t("creator_home.promotional.copy_code_aria", {
              code: promoCode,
            })}
            className="bg-muted hover:bg-muted/70 relative z-10 flex min-h-11 items-center justify-between gap-2 rounded-md border px-3 py-2 text-left transition-colors"
            data-testid="promotional-mission-card-promo-code"
          >
            <code className="font-mono text-base">{promoCode}</code>
            <span className="text-muted-foreground text-xs">
              {t("creator_home.promotional.code_copy_fallback")}
            </span>
          </button>
        </div>
      )}

      {ecommerceUrl ? (
        <Button
          asChild
          variant="default"
          className="relative z-10 h-11 w-full rounded-full"
          data-testid="promotional-mission-card-buy-on-brand"
        >
          <a
            href={ecommerceUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("creator_home.promotional.buy_on_brand_aria", {
              brand: collab.brand.name,
            })}
          >
            <ExternalLink aria-hidden="true" className="mr-1 size-4" />
            {t("creator_home.promotional.buy_on_brand_cta", {
              brand: collab.brand.name,
            })}
          </a>
        </Button>
      ) : (
        <p
          className="text-muted-foreground text-xs"
          data-testid="promotional-mission-card-store-url-missing"
        >
          {t("creator_home.promotional.ecommerce_url_missing_helper")}
        </p>
      )}
    </div>
  )
}
